import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { BASE_CURRENCY } from "@torre/assets";

import { formatMoney } from "../lib/format";
import { ASSET_STATUS_ICONS, langOf, statusLabel } from "../lib/vocab";
import { DEVICES_ROUTE } from "../paths";
import type { SummaryResponse } from "../services/activosApi";
import { StatCard } from "./shared";

// ============================================================================
// Resumen del inventario: conteos por estado, valor informativo (sólo con
// `feat:activos.costos`) y la tarjeta de sólo lectura de los equipos de rastreo
// (D1: los GPS siguen en Dispositivos; la cifra invertida sólo la ve el owner).
// Archivo común, idéntico en CC-CONSOLE y WAYAK.
// ============================================================================

const ORDER = ["en_uso", "en_almacen", "en_mantenimiento", "baja"] as const;

export function SummaryCards({ summary, canSeeCosts }: { summary: SummaryResponse; canSeeCosts: boolean }) {
  const { t, i18n } = useTranslation("activos");
  const lang = langOf(i18n.language);
  const tracking = summary.tracking;

  return (
    <div className="space-y-3" data-testid="activos-summary">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {ORDER.map((status) => (
          <StatCard
            key={status}
            icon={ASSET_STATUS_ICONS[status]}
            label={statusLabel(status, lang)}
            value={summary.byStatus?.[status] ?? 0}
            testId={`summary-${status}`}
          />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {canSeeCosts && summary.totalBase != null ? (
          <>
            <StatCard
              icon="ri-money-dollar-circle-line"
              label={t("summary.totalValue")}
              value={formatMoney(summary.totalBase, BASE_CURRENCY, lang)}
              hint={summary.withoutCost ? t("summary.withoutCost", { count: summary.withoutCost }) : t("summary.purchaseCost")}
              testId="summary-total"
            />
            <StatCard
              icon="ri-line-chart-line"
              label={t("summary.bookValue")}
              value={formatMoney(summary.bookValueBase, BASE_CURRENCY, lang)}
              hint={t("summary.bookValueHint")}
              testId="summary-book"
            />
          </>
        ) : null}
        <Link to={DEVICES_ROUTE} className="block" data-testid="summary-tracking">
          <StatCard
            icon="ri-gps-line"
            label={t("summary.tracking")}
            value={tracking?.devices ?? 0}
            hint={
              tracking?.investedBase != null
                ? t("summary.trackingInvested", { amount: formatMoney(tracking.investedBase, BASE_CURRENCY, lang) })
                : t("summary.trackingHint")
            }
          />
        </Link>
      </div>
      {summary.truncated ? <p className="text-xs text-content-muted">{t("summary.truncated")}</p> : null}
    </div>
  );
}
